import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Wallets context — injects the frontend's TanStack Query + Turnkey wallet
 * data into SDK auth hooks so `useWallets()` and `useActiveWallet()` work
 * without importing the Turnkey SDK or TanStack Query directly.
 *
 * Platform-neutral: pure React context + TypeScript interfaces. No web/RN
 * globals, no Turnkey, no TanStack. Safe in `src/auth/` core per
 * `.claude/rules/platform-agnostic.md`.
 *
 * Wiring: the frontend mounts a `<WalletsProvider>` in the provider stack
 * (above any component that calls `useWallets()`), driven by the
 * `useWallets()` hook from `@/queries/wallets`:
 *
 * ```tsx
 * // In frontend/src/providers/wallets-bridge.tsx
 * const walletsResult = useWalletsBase(isAuthenticated);
 * return (
 *   <WalletsProvider value={walletsResult}>
 *     {children}
 *   </WalletsProvider>
 * );
 * ```
 *
 * When no provider is mounted, `useWalletsContext()` returns an empty stub
 * so components that read the wallet list don't throw.
 */
import { createContext, useContext } from 'react';
// ── Stubs ────────────────────────────────────────────────────────────────────
const NOOP_ASYNC = () => Promise.reject(new Error('WalletsProvider not mounted'));
const NOOP_VOID_ASYNC = () => Promise.reject(new Error('WalletsProvider not mounted'));
const DEFAULT_WALLETS = Object.freeze({
    wallets: [],
    isLoading: false,
    error: null,
    createWallet: NOOP_ASYNC,
    importWallet: NOOP_ASYNC,
    exportWallet: NOOP_ASYNC,
    renameWallet: NOOP_ASYNC,
    unlinkWallet: NOOP_VOID_ASYNC,
    archiveWallet: NOOP_VOID_ASYNC,
    recoverWallet: NOOP_VOID_ASYNC,
    isCreating: false,
    isImporting: false,
    isArchiving: false,
    isRecovering: false,
});
const DEFAULT_ARCHIVED_WALLETS = Object.freeze({
    archivedWallets: [],
    isLoading: false,
    error: null,
});
// ── Wallets context ───────────────────────────────────────────────────────────
const WalletsContext = createContext(DEFAULT_WALLETS);
/** Returns the base wallet data and mutation methods from the nearest
 *  `<WalletsProvider>` in the tree. */
export function useWalletsContext() {
    return useContext(WalletsContext);
}
// ── Archived wallets context ──────────────────────────────────────────────────
const ArchivedWalletsContext = createContext(DEFAULT_ARCHIVED_WALLETS);
/** Returns the archived wallet data from the nearest `<ArchivedWalletsProvider>`. */
export function useArchivedWalletsContext() {
    return useContext(ArchivedWalletsContext);
}
/**
 * Provides the frontend wallet data and mutations to `useWallets()` and
 * `useActiveWallet()` below in the tree.
 *
 * Mount once in the frontend provider stack, driven by the TanStack Query-
 * and Turnkey-backed `useWallets()` from `@/queries/wallets`.
 */
export function WalletsProvider({ value, children }) {
    return _jsx(WalletsContext.Provider, { value: value, children: children });
}
/**
 * Provides the archived wallet list to `useArchivedWallets()` below in the tree.
 *
 * Mount once in the frontend provider stack, driven by the TanStack Query-
 * backed `useArchivedWallets()` from `@/queries/wallets`.
 */
export function ArchivedWalletsProvider({ value, children, }) {
    return (_jsx(ArchivedWalletsContext.Provider, { value: value, children: children }));
}
//# sourceMappingURL=wallets-context.js.map